'use client'

import { useState, Suspense } from 'react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { ArrowLeft } from '@/components/icons'
import { AuthShell, authFontStacks } from '@/components/auth/auth-shell'
import {
  AuthError,
  AuthField,
  AuthHeading,
  AuthPrimaryButton,
} from '@/components/auth/auth-form-primitives'

function ForgotPasswordForm() {
  const t = useTranslations('Auth')
  const tErrors = useTranslations('Errors')
  const searchParams = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || tErrors('somethingWentWrong'))
      }

      setSent(true)
    } catch (err: unknown) {
      console.error('[auth] Password reset request failed', err)
      setError(tErrors('somethingWentWrong'))
    } finally {
      setIsLoading(false)
    }
  }

  if (sent) {
    return (
      <AuthShell>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <AuthHeading title={t('resetLinkSent')} subtitle={t('resetLinkSentDescription')} />
          <Link
            href="/login"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              height: '52px',
              width: '100%',
              backgroundColor: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '12px',
              fontFamily: authFontStacks.body,
              fontWeight: 500,
              fontSize: '14px',
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={16} weight="regular" />
            {t('backToLogin')}
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}
      >
        <AuthHeading title={t('resetPassword')} subtitle={t('resetPasswordDescription')} />

        <AuthField
          id="email"
          type="email"
          autoComplete="email"
          label={t('emailLabel')}
          placeholder={t('emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {error ? <AuthError>{error}</AuthError> : null}

        <AuthPrimaryButton type="submit" isLoading={isLoading} disabled={isLoading}>
          {isLoading ? t('signingIn') : t('sendResetLink')}
        </AuthPrimaryButton>

        <div style={{ textAlign: 'center' }}>
          <Link
            href="/login"
            style={{
              fontFamily: authFontStacks.body,
              fontWeight: 500,
              fontSize: '14px',
              color: 'hsl(var(--muted-foreground))',
              textDecoration: 'none',
            }}
          >
            {t('backToLogin')}
          </Link>
        </div>
      </form>
    </AuthShell>
  )
}

export default function ForgotPasswordPage() {
  return (
    <Suspense
      fallback={
        <AuthShell>
          <div
            style={{ opacity: 0.35, display: 'flex', flexDirection: 'column', gap: '24px' }}
            aria-hidden="true"
          >
            <div
              style={{ height: '48px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
            />
            <div
              style={{ height: '52px', background: 'var(--hairline-faint)', borderRadius: '12px' }}
            />
          </div>
        </AuthShell>
      }
    >
      <ForgotPasswordForm />
    </Suspense>
  )
}
